import { createContext, useContext, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import api from "libs/api";
import redirectToCheckout from "libs/helpers/redirectToCheckout";
import tax_data from "libs/tax_data.json";

const NewWebsiteContext = createContext();

const NewWebsiteProvider = ({ children, change_plan_mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(0);
  const [step_touched, setStepTouched] = useState(0);
  const [form_data, setFormData] = useState(null);
  const [form_data_error, setFormDataError] = useState("");

  const { isLoading: loading, data: website } = useQuery({
    queryKey: ["websites", id],
    queryFn: () => api.get(`/client/websites/${id}`),
    enabled: !!id && !!change_plan_mode,
  });

  const saveData = (values) => {
    setFormData((prev) => ({ ...prev, ...values }));
  };

  const nextStep = (values) => {
    setStep((prev) => prev + 1);
    if (step === step_touched) {
      setStepTouched((prev) => prev + 1);
    }
    saveData(values);
  };

  const prevStep = (values) => {
    setStep((prev) => prev - 1);

    if (values) saveData(values);
  };

  const createPayment = async () => {
    try {
      const data_to_send = { ...form_data };

      const tax = tax_data?.find(
        (item) => data_to_send?.billing?.tax?.code === item?.code,
      );

      const createWebsite = await api.post(`/client/websites`, {
        ...data_to_send,
        billing: {
          ...data_to_send?.billing,
          tax: {
            ...data_to_send?.billing?.tax,
            prefix: tax?.prefix,
            clean_number: data_to_send.billing.tax?.number,
            number: `${tax?.prefix || ""}${data_to_send.billing.tax?.number}`,
            id: tax?.tax_id,
          },
        },
      });
      redirectToCheckout(createWebsite);
    } catch (err) {
      if (err?.response?.data?.error === "tax_id_invalid") {
        setStep(2);
        setFormDataError(err?.response?.data?.error);
      }
      throw err;
    }
  };

  const updateSubscription = async () => {
    if (!!website?.subscription?.subscription_id) {
      let subscriptionUpdate;

      try {
        subscriptionUpdate = await api.put(
          `/client/websites/${id}/subscription`,
          form_data,
        );
      } catch (err) {
        toast.dismiss();
        toast.error(t("Failed to change the plan. Please try again."));
        throw err;
      }

      queryClient.setQueryData(["websites", id], (prev) => ({
        ...prev,
        subscription: {
          ...prev?.subscription,
          price_id: subscriptionUpdate?.price_id,
        },
      }));
      queryClient.invalidateQueries({ queryKey: ["websites"] });

      setStep(5);
    } else {
      const continuePayment = await api.post(
        `/client/websites/${website?._id}/continue-payment`,
        {
          price_id: form_data?.plan,
        },
      );
      redirectToCheckout(continuePayment);
    }
  };

  const submitForm = async () => {
    if (!!change_plan_mode) {
      await updateSubscription();
    } else {
      await createPayment();
    }
  };

  const value = useMemo(() => {
    return {
      saveData,
      step,
      step_touched,
      form_data,
      website,
      change_plan_mode,
      loading: !!loading && !!change_plan_mode,
      nextStep,
      prevStep,
      setStep,
      form_data_error,
      submitForm,
    };
    // eslint-disable-next-line
  }, [
    saveData,
    step,
    step_touched,
    form_data,
    website,
    change_plan_mode,
    form_data_error,
    loading,
  ]);

  return (
    <NewWebsiteContext.Provider value={value}>
      {children}
    </NewWebsiteContext.Provider>
  );
};

const useNewWebsite = () => useContext(NewWebsiteContext);
export { NewWebsiteContext, useNewWebsite };
export default NewWebsiteProvider;
